import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addDays,
  isSameDay,
} from 'date-fns';
import CalendarDayCell from './CalendarDayCell';
import CalendarDayTimeline from './CalendarDayTimeline';
import type { CalendarView as CalendarViewType, ScheduledPostEntry } from '../../types/calendar';

interface CalendarViewProps {
  /** Current view mode (day/week/month) */
  view: CalendarViewType;
  /** Currently selected/focused date */
  selectedDate: Date;
  /** Scheduled posts grouped by date (ISO date string -> posts) */
  posts: Record<string, ScheduledPostEntry[]>;
  /** Loading state */
  isLoading?: boolean;
  /** Handler for clicking on a post */
  onPostClick?: (post: ScheduledPostEntry) => void;
  /** Handler for clicking to add a post on a date */
  onAddPost?: (date: Date) => void;
  /** Handler for rescheduling a post */
  onReschedulePost?: (post: ScheduledPostEntry) => void;
  /** Handler for unscheduling a post */
  onUnschedulePost?: (post: ScheduledPostEntry) => void;
  /** Optional map of post IDs to titles */
  postTitles?: Record<string, string>;
}

/**
 * CalendarView component
 * Renders the calendar grid based on the selected view (day/week/month).
 * Uses CalendarDayCell components to display individual days with their posts.
 */
export default function CalendarView({
  view,
  selectedDate,
  posts,
  isLoading = false,
  onPostClick,
  onAddPost,
  onReschedulePost,
  onUnschedulePost,
  postTitles = {},
}: CalendarViewProps) {
  const { t } = useTranslation();

  /**
   * Calculate the days to display based on current view
   */
  const days = useMemo(() => {
    switch (view) {
      case 'day':
        return [selectedDate];

      case 'week': {
        // Week starts on Monday (weekStartsOn: 1)
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
        return eachDayOfInterval({ start: weekStart, end: weekEnd });
      }

      case 'month': {
        // Get the full month
        const monthStart = startOfMonth(selectedDate);
        const monthEnd = endOfMonth(selectedDate);

        // Extend to include days from previous/next month to fill the grid
        const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

        return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
      }

      default:
        return [selectedDate];
    }
  }, [view, selectedDate]);

  /**
   * Get posts for a specific date
   */
  const getPostsForDate = (date: Date): ScheduledPostEntry[] => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return posts[dateKey] || [];
  };

  /**
   * Get weekday headers
   */
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => {
      const day = addDays(weekStart, i);
      return {
        short: format(day, 'EEE'),
        full: format(day, 'EEEE'),
      };
    });
  }, []);

  /**
   * Check if calendar has any posts
   */
  const hasAnyPosts = useMemo(() => {
    return Object.values(posts).some((dayPosts) => dayPosts.length > 0);
  }, [posts]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Day view - 24-hour timeline layout
  if (view === 'day') {
    return (
      <div className="flex-1 flex flex-col">
        {/* Day header */}
        <div className="mb-4 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {format(selectedDate, 'EEEE')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {format(selectedDate, 'MMMM d, yyyy')}
          </p>
        </div>

        {/* Day timeline */}
        <CalendarDayTimeline
          date={selectedDate}
          posts={getPostsForDate(selectedDate)}
          onPostClick={onPostClick}
          onAddPost={(date, hour) => {
            // Create a date with the selected hour
            const scheduledDate = new Date(date);
            scheduledDate.setHours(hour, 0, 0, 0);
            onAddPost?.(scheduledDate);
          }}
          onReschedulePost={onReschedulePost}
          onUnschedulePost={onUnschedulePost}
        />
      </div>
    );
  }

  // Week view - 7 column grid
  if (view === 'week') {
    return (
      <div className="flex-1 flex flex-col">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day.short}
              className="text-center py-2 text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider"
            >
              <span className="hidden sm:inline">{day.full}</span>
              <span className="sm:hidden">{day.short}</span>
            </div>
          ))}
        </div>

        {/* Week grid */}
        <div className="flex-1 grid grid-cols-7 gap-1">
          {days.map((day) => (
            <div key={format(day, 'yyyy-MM-dd')} className="group">
              <CalendarDayCell
                date={day}
                posts={getPostsForDate(day)}
                selectedDate={selectedDate}
                view={view}
                onPostClick={onPostClick}
                onAddPost={onAddPost}
                onReschedulePost={onReschedulePost}
                onUnschedulePost={onUnschedulePost}
                postTitles={postTitles}
              />
            </div>
          ))}
        </div>

        {/* Empty state for week view */}
        {!hasAnyPosts && (
          <div className="mt-4 p-4 text-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400">
              {t('calendar.empty.weekDescription')}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Month view - 7 column grid with multiple weeks
  return (
    <div className="flex-1 flex flex-col">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div
            key={day.short}
            className="text-center py-2 text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider"
          >
            <span className="hidden sm:inline">{day.short}</span>
            <span className="sm:hidden">{day.short.charAt(0)}</span>
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div
        className="flex-1 grid grid-cols-7 auto-rows-fr gap-1"
        style={{ minHeight: '500px' }}
      >
        {days.map((day) => {
          const isCurrentMonth = isSameDay(startOfMonth(day), startOfMonth(selectedDate)) ||
            (day >= startOfMonth(selectedDate) && day <= endOfMonth(selectedDate));

          return (
            <div
              key={format(day, 'yyyy-MM-dd')}
              className={`group ${!isCurrentMonth ? 'opacity-60' : ''}`}
            >
              <CalendarDayCell
                date={day}
                posts={getPostsForDate(day)}
                selectedDate={selectedDate}
                view={view}
                onPostClick={onPostClick}
                onAddPost={onAddPost}
                onReschedulePost={onReschedulePost}
                onUnschedulePost={onUnschedulePost}
                postTitles={postTitles}
              />
            </div>
          );
        })}
      </div>

      {/* Empty state for month view */}
      {!hasAnyPosts && (
        <div className="mt-4 p-4 text-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">
            {t('calendar.empty.monthDescription')}
          </p>
        </div>
      )}
    </div>
  );
}
